/**
 * The document head, per page and per locale.
 *
 * The Worker writes `lang`, the canonical URL and the hreflang cluster into the
 * shell for the URL that was requested (worker/lib/locale.ts). After a
 * client-side navigation those links still describe the first page, so the
 * router calls `syncHeadLinks` on every change of path or locale and this
 * rewrites them — creating them when the shell has none, which is the dev
 * server's case.
 */

import { useEffect } from 'react';
import { DEFAULT_LOCALE, LOCALES } from '@shared/locales';

function upsertLink(selector: string, attrs: Record<string, string>): void {
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
}

export function syncHeadLinks(locale: string, path: string): void {
  const base = window.location.origin;
  for (const l of LOCALES) {
    upsertLink(`link[rel="alternate"][hreflang="${l.intl}"]`, {
      rel: 'alternate',
      hreflang: l.intl,
      href: `${base}/${l.tag}${path}`
    });
  }
  upsertLink('link[rel="alternate"][hreflang="x-default"]', {
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${base}/${DEFAULT_LOCALE}${path}`
  });
  upsertLink('link[rel="canonical"]', { rel: 'canonical', href: `${base}/${locale}${path}` });
}

/** The title and description of the page being rendered. Call once per page. */
export function useDocumentMeta(title: string, description?: string): void {
  useEffect(() => {
    document.title = title;
    if (description !== undefined) {
      let meta = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', description);
    }
  }, [title, description]);
}
